import { AfterViewInit, Component, ElementRef, inject } from '@angular/core'
import { AsyncPipe, CommonModule, NgFor, NgIf } from '@angular/common'
import { Router, ActivatedRoute } from '@angular/router'
import {
	AuthService,
	InfiniteScrollService,
	VenueFilter,
	VenuesGateway,
	getClient,
	getFilterVenues,
	getStatusClient,
	loadingMoreVenues,
} from '@monorepo-channels/channels/domain'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { combineLatest, filter, first, map, of, tap } from 'rxjs'
import { getStatusVenues, getVenues, VenueActions } from '@monorepo-channels/channels/domain'
import { Store } from '@ngrx/store'
import { GeolocationService, MenuOpened } from '@monorepo-channels/shared/util-helpers'
import { MatIconModule } from '@angular/material/icon'
import { LoadingPageComponent, VenueCardUiComponent } from '@monorepo-channels/components/ui-porto-bank'

@Component({
	selector: 'feature-porto-bank-venue-card-list',
	standalone: true,
	imports: [
		NgIf,
		NgFor,
		AsyncPipe,
		VenueCardUiComponent,
		MatIconModule,
		LoadingPageComponent,
		CommonModule,
	],
	styles: `
        body.all {
            :host {
                --content-primary: white;
            }
        }
        @import 'mixin';
    .container {
        display: grid;
        grid-template-columns: 1fr;
        gap: 24px;
        width: 100%;
        max-width: 100%;
        padding: 0 20px;
        margin: 0 auto;
        box-sizing: border-box;
        min-width: 0;

        &.menu-opened {
            width: 100% !important;
        }

        @include  laptop-up {
            width: 100%;
            max-width: 900px;
            margin: 0 auto;
            padding: 0;
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }
        
        @include desktop-up {
            display: grid;
            grid-template-columns: repeat(var(--number-of-cards, 3), minmax(0, 1fr));
            gap: 32px;
            width: 100%;
            max-width: var(--max-width-venues);
            padding: 0;
            
            &.menu-opened {
                width: 100% !important;
            }
        }
    }

    `,
	template: `
		<div [class.menu-opened]="menuOpened.sidenavOpened" class="container">
			@if (statusVenues$ | async; as status) {
				@if (status === 'pending' || status === 'idle') {
					<div style="padding: 58px 0;">
						<ui-porto-bank-loading-page />
					</div>
				} @else {
					<ng-container *ngIf="vm$ | async as vm">
						<div
							style="
                            position: absolute;
                            display: flex; 
                            flex-direction: column; 
                            align-items: center; 
                            text-align: center; 
                            color: var(--content-txt); 
                            padding: 20px 0;
                            left: 50%;
                            transform: translateX(-50%);
                            "
							*ngIf="vm.venues?.length === 0"
						>
							<mat-icon
								svgIcon="porto-bank-icons:close"
								style=" width: 18px; height: 18px; margin-bottom: 10px;"
							></mat-icon>
							<p
								style="margin: 0;font-size: 16px; font-weight: 600; color: var(--content-title)"
							>
								Restaurante não encontrado.
							</p>
							<p style="color: var(--content-txt-secondary)">Refaça sua busca.</p>
						</div>

						@if (vm.filterVenues && vm.filterVenues.length > 0) {
							<ui-porto-bank-venue-card
								*ngFor="let venue of vm.filterVenues"
								[venue]="venue"
								[client]="vm.client"
								(clickCard)="clickCard(venue)"
							/>
						} @else {
							<ui-porto-bank-venue-card
								*ngFor="let venue of vm.venues"
								[venue]="venue"
								[client]="vm.client"
								(clickCard)="clickCard(venue)"
							/>
						}

						<section style="height: 120px; width: 100%"></section>
					</ng-container>
				}
			}
		</div>
	`,
})
export class VenueCardListComponent implements AfterViewInit {
	private store = inject(Store)
	public router = inject(Router)
	public activatedRoute = inject(ActivatedRoute)
	public client = this.store.select(getClient)
	private authService = inject(AuthService)
	private el = inject(ElementRef)
	public loadingMoreVenues$ = this.store.select(loadingMoreVenues)
	private infiniteScrollService = inject(InfiniteScrollService)
	private venueGateway = inject(VenuesGateway)
	public menuOpened = inject(MenuOpened)

	public venues$ = this.store.select(getVenues)
	public filterVenues$ = this.store.select(getFilterVenues)
	public vm$ = combineLatest([this.venues$, this.filterVenues$, this.client]).pipe(
		map(([venues, filterVenues, client]) => ({
			venues,
			filterVenues,
			client,
		}))
	)
	public statusVenues$ = this.store.select(getStatusVenues).pipe(
		tap(status => {
			if (status === 'pending' || status === 'idle') {
				this.el.nativeElement.style.setProperty('--number-of-cards', '1')
			} else {
				this.el.nativeElement.style.setProperty('--number-of-cards', '3')
			}
		})
	)
	public statusClient$ = this.store.select(getStatusClient)

	constructor(private geoLocation: GeolocationService) {
		this.infiniteScrollService.loadMoreVenue$
			.pipe(
				filter(loadMore => loadMore),
				takeUntilDestroyed()
			)
			.subscribe(() => {
				this.store.dispatch(VenueActions.loadMoreVenue())
			})

		// Get currentLocation
		this.geoLocation.getCurrentLocation()

		const { clientId } = this.authService.getClientIdAndToken()

		const clientStatus$ = clientId
			? this.statusClient$.pipe(
					filter(status => status === 'success' || status === 'failure'),
					first()
			  )
			: of('no-login')

		// Combina status$ e location$ e espera o estado apropriado
		combineLatest([clientStatus$, this.geoLocation.location$])
			.pipe(
				takeUntilDestroyed(),
				filter(([, { state }]) => state !== 'idle'), // Ignora enquanto `location$` estiver em 'idle'
				first()
			)
			.subscribe(([, { state, position }]) => {
				if (state === 'denied' || state === 'prompt') {
					this.store.dispatch(VenueActions.getVenues())
				} else if (state === 'granted' && position) {
					this.store.dispatch(
						VenueActions.setLocation({
							latitude: position.latitude,
							longitude: position.longitude,
							maxDistance: 42_500_000,
						})
					)
				}
			})
	}

	clickCard(venue: VenueFilter) {
		this.router.navigate(['/restaurante', venue.id])
	}

	ngAfterViewInit(): void {
		const scrollRoot =
			document.getElementById('portoBankMainScroll') ??
			(document.querySelector('mat-sidenav-content') as HTMLElement | null)
		if (!scrollRoot) return

		this.infiniteScrollService.setScrollTarget(scrollRoot)
	}

	loadMoreVenues() {
		this.store.dispatch(VenueActions.loadMoreVenue())
	}
}
